import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuthStore } from '../store/auth';
import { useDialog } from './Dialog';

// Primeros pasos pensados para freelancers que cobran en dólares:
// cuenta en USD, primer cobro de un cliente, RFC e importación de CFDI.
const STEPS = [
  { id: 'bienvenida', titulo: '¡Bienvenido a MONIMEX!',        emoji: '🎉' },
  { id: 'cuenta-usd', titulo: 'Crea tu cuenta en dólares',       emoji: '💵' },
  { id: 'cobro',      titulo: 'Registra el pago de un cliente',  emoji: '🧾' },
  { id: 'rfc',        titulo: 'Captura tu RFC',                  emoji: '🪪' },
  { id: 'cfdi',       titulo: 'Importa tus CFDI',                emoji: '📥' },
  { id: 'listo',      titulo: '¡Todo listo!',                    emoji: '🚀' },
];

const inputCls = 'w-full px-4 py-3 rounded-xl border-2 border-gray-200 dark:border-slate-700 dark:bg-slate-800 dark:text-white focus:border-blue-600 dark:focus:border-blue-400 outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:focus-visible:ring-blue-400 transition';
const backCls = 'flex-1 border-2 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-400 font-medium py-3 rounded-xl hover:border-gray-300 transition';
const nextCls = 'flex-[2] bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition active:scale-95';

const NADA = () => {};

export default function Onboarding({ onComplete }) {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [step, setStep]         = useState(0);
  const panelRef = useRef(null);
  // Foco dentro y Tab atrapado. Escape no hace nada: saltar la configuración se guarda en el
  // servidor y debe ser una decisión explícita ("Saltar configuración").
  useDialog(panelRef, true, NADA);
  const [animDir, setAnimDir]   = useState('enter');
  const [cuentaNombre, setCuentaNombre] = useState('Cuenta USD');
  const [cuentaUsdId, setCuentaUsdId]   = useState(null);
  const [monto, setMonto]       = useState('');
  const [cliente, setCliente]   = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const goNext = () => {
    setError('');
    setAnimDir('exit');
    setTimeout(() => { setStep(s => s + 1); setAnimDir('enter'); }, 220);
  };

  const goBack = () => {
    setError('');
    setAnimDir('exit-back');
    setTimeout(() => { setStep(s => s - 1); setAnimDir('enter-back'); }, 220);
  };

  const crearCuentaUsd = async () => {
    if (!cuentaNombre.trim()) { setError('Escribe el nombre de tu cuenta'); return; }
    setLoading(true); setError('');
    try {
      const res = await api.post('/cuentas', { nombre: cuentaNombre.trim(), tipo: 'debito', moneda: 'USD' });
      setCuentaUsdId(res.data?.id ?? null);
      goNext();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al crear la cuenta');
    } finally { setLoading(false); }
  };

  const registrarCobro = async () => {
    if (!monto || parseFloat(monto) <= 0) { setError('Ingresa un monto válido en dólares'); return; }
    setLoading(true); setError('');
    try {
      await api.post('/transacciones', {
        tipo: 'ingreso', monto: parseFloat(monto), divisa: 'USD',
        ...(cuentaUsdId ? { cuenta_id: cuentaUsdId } : {}),
        descripcion: cliente.trim() ? `Pago de ${cliente.trim()}` : 'Pago de cliente',
        fecha_transaccion: new Date().toISOString().split('T')[0],
        estado: 'completada',
      });
      goNext();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al registrar el pago');
    } finally { setLoading(false); }
  };

  const terminar = async () => {
    try { await api.post('/me/onboarding-done'); } catch {}
    onComplete?.();
    navigate('/dashboard');
  };

  // Cierra el onboarding y lleva a otra pantalla (RFC en Perfil, CFDI, etc.)
  const irA = (ruta) => {
    api.post('/me/onboarding-done').catch(() => {});
    onComplete?.();
    navigate(ruta);
  };

  const progress = ((step) / (STEPS.length - 1)) * 100;

  const animClass = {
    'enter':      'opacity-100 translate-x-0',
    'enter-back': 'opacity-100 translate-x-0',
    'exit':       'opacity-0 translate-x-8',
    'exit-back':  'opacity-0 -translate-x-8',
  }[animDir] || 'opacity-100 translate-x-0';

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4 bg-gradient-to-br from-blue-600 to-indigo-700 outline-none"
      ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Primeros pasos en MONIMEX">
      {/* Decorative blobs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl pointer-events-none" />

      <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden relative">
        {/* Progress bar */}
        <div className="h-1.5 bg-gray-100 dark:bg-slate-800">
          <div className="h-full bg-blue-600 transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }} />
        </div>

        <div className="px-8 py-8">
          {/* Step indicators */}
          <div className="flex justify-center gap-2 mb-6">
            {STEPS.map((s, i) => (
              <div key={s.id} className={`w-2 h-2 rounded-full transition-all duration-300 ${
                i === step ? 'bg-blue-600 w-6' : i < step ? 'bg-blue-300' : 'bg-gray-200 dark:bg-slate-700'
              }`} />
            ))}
          </div>

          {/* Step content with animation */}
          <div className={`transition-all duration-200 ease-out ${animClass}`}>

            {/* Step 0: Bienvenida */}
            {step === 0 && (
              <div className="text-center">
                <div className="text-6xl mb-4 animate-bounce">{STEPS[0].emoji}</div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  ¡Hola, {user?.usuario}!
                </h1>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  {user?.plan === 'pro' ? 'Empiezas con 14 días de Pro, sin tarjeta. ' : ''}Configura tu cuenta de freelancer en unos minutos.
                </p>
                <div className="grid grid-cols-3 gap-3 mb-8">
                  {[
                    { icon: '💵', label: 'Cuenta en USD' },
                    { icon: '🧾', label: 'Cobros de clientes' },
                    { icon: '📥', label: 'CFDI del SAT' },
                  ].map(f => (
                    <div key={f.label} className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 text-center">
                      <div className="text-2xl mb-1">{f.icon}</div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{f.label}</p>
                    </div>
                  ))}
                </div>
                <button onClick={goNext}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition active:scale-95">
                  Comenzar →
                </button>
                <button onClick={terminar} className="mt-3 text-xs text-gray-400 hover:text-gray-600 transition">
                  Saltar configuración
                </button>
              </div>
            )}

            {/* Step 1: Cuenta en USD */}
            {step === 1 && (
              <div>
                <div className="text-center mb-6">
                  <div className="text-5xl mb-3">{STEPS[1].emoji}</div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">{STEPS[1].titulo}</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Para los clientes que te pagan en USD (Payoneer, Wise, PayPal o banco). Convertimos a pesos con el tipo de cambio del día.
                  </p>
                </div>
                <div className="space-y-3 mb-6">
                  {['Cuenta USD', 'Wise', 'Payoneer', 'PayPal'].map(sugerencia => (
                    <button key={sugerencia}
                      onClick={() => setCuentaNombre(sugerencia)}
                      className={`w-full text-left px-4 py-3 rounded-xl border-2 transition ${
                        cuentaNombre === sugerencia
                          ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                          : 'border-gray-200 dark:border-slate-700 hover:border-blue-300'
                      }`}>
                      {sugerencia}
                    </button>
                  ))}
                  <input value={cuentaNombre} onChange={e => setCuentaNombre(e.target.value)}
                    placeholder="O escribe el nombre..." aria-label="Nombre de la cuenta" className={inputCls} />
                </div>
                {error && <p className="text-red-500 dark:text-red-400 text-sm mb-3">{error}</p>}
                <div className="flex gap-2">
                  <button onClick={goBack} className={backCls}>← Atrás</button>
                  <button onClick={crearCuentaUsd} disabled={loading} className={nextCls}>
                    {loading ? 'Creando...' : 'Crear cuenta USD →'}
                  </button>
                </div>
                <button onClick={goNext} className="mt-3 w-full text-xs text-gray-400 hover:text-gray-600 transition">
                  Solo cobro en pesos, saltar
                </button>
              </div>
            )}

            {/* Step 2: Primer pago de cliente */}
            {step === 2 && (
              <div>
                <div className="text-center mb-6">
                  <div className="text-5xl mb-3">{STEPS[2].emoji}</div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">{STEPS[2].titulo}</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Anota un ingreso en dólares. Verás cuánto equivale en pesos.
                  </p>
                </div>
                <div className="space-y-3 mb-4">
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 font-semibold">US$</span>
                    <input type="number" aria-label="Monto en dólares" value={monto} onChange={e => setMonto(e.target.value)} placeholder="0.00"
                      className="w-full pl-14 pr-4 py-3 rounded-xl border-2 border-gray-200 dark:border-slate-700 dark:bg-slate-800 dark:text-white focus:border-blue-600 dark:focus:border-blue-400 outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:focus-visible:ring-blue-400 transition text-lg font-semibold" />
                  </div>
                  <input value={cliente} onChange={e => setCliente(e.target.value)} placeholder="Cliente (ej: Acme Inc.)" aria-label="Cliente"
                    className={inputCls} />
                </div>
                {error && <p className="text-red-500 dark:text-red-400 text-sm mb-3">{error}</p>}
                <div className="flex gap-2">
                  <button onClick={goBack} className={backCls}>← Atrás</button>
                  <button onClick={registrarCobro} disabled={loading} className={nextCls}>
                    {loading ? 'Guardando...' : 'Guardar pago →'}
                  </button>
                </div>
                <button onClick={goNext} className="mt-3 w-full text-xs text-gray-400 hover:text-gray-600 transition">
                  Saltar este paso
                </button>
              </div>
            )}

            {/* Step 3: RFC */}
            {step === 3 && (
              <div className="text-center">
                <div className="text-5xl mb-3">{STEPS[3].emoji}</div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{STEPS[3].titulo}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 mb-6">
                  Con tu RFC podemos identificar tus facturas emitidas y recibidas al importar CFDI. Lo capturas en tu Perfil.
                </p>
                <button onClick={() => irA('/perfil')}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition active:scale-95 mb-2">
                  Capturar mi RFC en Perfil
                </button>
                <div className="flex gap-2">
                  <button onClick={goBack} className={backCls}>← Atrás</button>
                  <button onClick={goNext} className={nextCls}>Lo hago después →</button>
                </div>
              </div>
            )}

            {/* Step 4: Importar CFDI */}
            {step === 4 && (
              <div className="text-center">
                <div className="text-5xl mb-3">{STEPS[4].emoji}</div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{STEPS[4].titulo}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 mb-6">
                  Sube los XML de tus facturas del SAT y se convierten en movimientos, sin capturar nada a mano.
                </p>
                <button onClick={() => irA('/cfdi')}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition active:scale-95 mb-2">
                  Importar CFDI ahora
                </button>
                <div className="flex gap-2">
                  <button onClick={goBack} className={backCls}>← Atrás</button>
                  <button onClick={goNext} className={nextCls}>Lo hago después →</button>
                </div>
              </div>
            )}

            {/* Step 5: Listo */}
            {step === 5 && (
              <div className="text-center">
                <div className="text-6xl mb-4" style={{ animation: 'spin-once 0.5s ease-out' }}>
                  {STEPS[5].emoji}
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{STEPS[5].titulo}</h2>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  Ya puedes ver tu panel, registrar más cobros y gastos, e importar tus CFDI cuando quieras.
                </p>
                <div className="grid grid-cols-2 gap-3 mb-8 text-left">
                  {[
                    { icon: '📊', label: 'Panel',          ruta: '/dashboard' },
                    { icon: '💼', label: 'Proyectos',      ruta: '/proyectos' },
                    { icon: '🧾', label: 'Importar CFDI',  ruta: '/cfdi' },
                    { icon: '⭐', label: 'Ver planes',     ruta: '/planes' },
                  ].map(r => (
                    <button key={r.ruta} onClick={() => irA(r.ruta)}
                      className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-slate-800 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl transition text-sm font-medium text-gray-700 dark:text-gray-300">
                      <span className="text-xl">{r.icon}</span> {r.label}
                    </button>
                  ))}
                </div>
                <button onClick={terminar}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition active:scale-95">
                  Ir al panel →
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin-once {
          from { transform: rotate(-30deg) scale(0.5); opacity: 0; }
          to   { transform: rotate(0deg) scale(1); opacity: 1; }
        }
        .animate-scale-in { animation: scale-in 0.2s ease-out; }
        @keyframes scale-in {
          from { transform: scale(0.9); opacity: 0; }
          to   { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
